import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AirportSearch from './AirportSearch';

const TRAVEL_CLASSES = [
  { value: 'economy', label: 'Economy' },
  { value: 'business', label: 'Business' },
  { value: 'firstClass', label: 'First Class' },
];

/* ── Passenger counter row ─────────────────────────────── */
function PaxRow({ label, sub, value, onInc, onDec, min = 0 }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        <p className="text-xs text-gray-400">{sub}</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onDec}
          disabled={value <= min}
          className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center text-lg leading-none hover:border-blue-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >−</button>
        <span className="w-5 text-center text-sm font-bold text-gray-900">{value}</span>
        <button
          type="button"
          onClick={onInc}
          disabled={value >= 9}
          className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center text-lg leading-none hover:border-blue-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >+</button>
      </div>
    </div>
  );
}

/* ── Passenger dropdown ────────────────────────────────── */
function PassengerPicker({ adults, children, infants, onChange }) {
  const [open, setOpen] = useState(false);
  const total = adults + children + infants;

  const set = (field, val) => onChange({ adults, children, infants, [field]: val });

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-white/70 mb-1">Passengers</label>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full bg-white/10 border rounded-xl px-3 py-2.5 text-left text-sm text-white flex items-center justify-between transition-all ${
          open ? 'border-orange-400 ring-2 ring-amber-400/20' : 'border-white/20 hover:border-white/40'
        }`}
      >
        <span>
          {total} Traveller{total !== 1 ? 's' : ''}
          <span className="text-white/40 text-xs ml-1">
            ({adults} ADT{children > 0 ? `, ${children} CHD` : ''}{infants > 0 ? `, ${infants} INF` : ''})
          </span>
        </span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-4 divide-y divide-gray-100">
          <PaxRow
            label="Adults" sub="Age 12+"
            value={adults} min={1}
            onInc={() => set('adults', adults + 1)}
            onDec={() => set('adults', adults - 1)}
          />
          <PaxRow
            label="Children" sub="Age 2–11"
            value={children} min={0}
            onInc={() => set('children', children + 1)}
            onDec={() => set('children', children - 1)}
          />
          <PaxRow
            label="Infants" sub="Under 2 · on lap"
            value={infants} min={0}
            onInc={() => set('infants', Math.min(infants + 1, adults))}
            onDec={() => set('infants', infants - 1)}
          />
          <div className="pt-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
            >Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main form ─────────────────────────────────────────── */
export default function SearchForm({ initialValues = {} }) {
  const navigate = useNavigate();

  const [tripType, setTripType] = useState(initialValues.tripType || 'oneway');
  const [form, setForm] = useState({
    origin: initialValues.origin || '',
    destination: initialValues.destination || '',
    date: initialValues.date || '',
    returnDate: initialValues.returnDate || '',
    class: initialValues.class || 'economy',
  });
  const [pax, setPax] = useState({
    adults: parseInt(initialValues.adults) || 1,
    children: parseInt(initialValues.children) || 0,
    infants: parseInt(initialValues.infants) || 0,
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleAirport = (field) => (code) => {
    setForm(prev => ({ ...prev, [field]: code }));
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.origin || !form.destination || !form.date) {
      setError('Please fill in all required fields.');
      return;
    }
    if (form.origin === form.destination) {
      setError('Origin and destination cannot be the same.');
      return;
    }
    if (tripType === 'roundtrip' && !form.returnDate) {
      setError('Please select a return date.');
      return;
    }
    if (tripType === 'roundtrip' && form.returnDate < form.date) {
      setError('Return date must be after departure date.');
      return;
    }
    const params = new URLSearchParams({
      origin: form.origin,
      destination: form.destination,
      date: form.date,
      ...(tripType === 'roundtrip' && form.returnDate ? { returnDate: form.returnDate } : {}),
      adults: pax.adults,
      children: pax.children,
      infants: pax.infants,
      passengers: pax.adults + pax.children + pax.infants,
      class: form.class,
      tripType,
    }).toString();
    navigate(`/flights?${params}`);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <form onSubmit={handleSubmit}>
      {/* Trip type toggle */}
      <div className="flex gap-1 mb-5 bg-white/10 rounded-xl p-1 w-fit">
        {[['oneway', '→ One Way'], ['roundtrip', '⇄ Round Trip']].map(([val, label]) => (
          <button
            key={val}
            type="button"
            onClick={() => setTripType(val)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
              tripType === val
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-white/60 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
        {/* From */}
        <div className={tripType === 'roundtrip' ? 'lg:col-span-1' : 'lg:col-span-1'}>
          <AirportSearch
            label="From"
            name="origin"
            value={form.origin}
            onChange={handleAirport('origin')}
            placeholder="City or IATA code"
          />
        </div>

        {/* To */}
        <div className="lg:col-span-1">
          <AirportSearch
            label="To"
            name="destination"
            value={form.destination}
            onChange={handleAirport('destination')}
            placeholder="City or IATA code"
          />
        </div>

        {/* Depart date */}
        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-white/70 mb-1">Depart</label>
          <input
            type="date"
            name="date"
            value={form.date}
            min={today}
            onChange={handleChange}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            required
          />
        </div>

        {/* Return date — only for round trip */}
        {tripType === 'roundtrip' && (
          <div className="lg:col-span-1">
            <label className="block text-sm font-medium text-white/70 mb-1">Return</label>
            <input
              type="date"
              name="returnDate"
              value={form.returnDate}
              min={form.date || today}
              onChange={handleChange}
              className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              required={tripType === 'roundtrip'}
            />
          </div>
        )}

        {/* Passengers */}
        <div className="lg:col-span-1">
          <PassengerPicker
            adults={pax.adults}
            children={pax.children}
            infants={pax.infants}
            onChange={setPax}
          />
        </div>

        {/* Class */}
        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-white/70 mb-1">Class</label>
          <select
            name="class"
            value={form.class}
            onChange={handleChange}
            className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            {TRAVEL_CLASSES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className="text-red-300 text-sm mt-3">{error}</p>}

      <div className="mt-4 sm:mt-5">
        <button
          type="submit"
          className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-navy-950 font-bold px-8 sm:px-10 py-3.5 rounded-xl transition-all text-base shadow-xl shadow-navy-900/30 hover:-translate-y-0.5"
        >
          Search Flights →
        </button>
      </div>
    </form>
  );
}
